import type {
  DeviceAddress,
  DeviceKeyOf,
  ReadDeviceEditArgs,
  WithoutDeviceKeysArgs,
} from "./types";

export const deviceKeyOf: DeviceKeyOf = ({ elementId, destinationId }) =>
  elementId?.trim() || destinationId;

/**
 * Read an edit under either key — the durable one first, the legacy address
 * second.
 *
 * Dual-read is what makes this migration free: a document written before the
 * key changed still applies, and nothing has to rewrite it with the geometry in
 * hand. Documents converge on the durable key as he touches them.
 */
export const readDeviceEdit = <T>({
  edits,
  address,
}: ReadDeviceEditArgs<T>): T | undefined => {
  if (!edits) return undefined;
  const durable = address.elementId?.trim();
  if (durable && durable in edits) return edits[durable];
  return edits[address.destinationId];
};

/** Drop an edit under BOTH keys: a restore must not leave the legacy one behind. */
export const withoutDeviceKeys = <T>({
  edits,
  address,
}: WithoutDeviceKeysArgs<T>): Record<string, T> => {
  const next = { ...(edits ?? {}) };
  const durable = address.elementId?.trim();
  if (durable) delete next[durable];
  delete next[address.destinationId];
  return next;
};

/** Both forms, for a caller that has to clear or compare across the change. */
export const deviceKeysOf = (address: DeviceAddress): string[] => {
  const durable = address.elementId?.trim();
  return durable && durable !== address.destinationId
    ? [durable, address.destinationId]
    : [address.destinationId];
};
